import { fglGrammar } from './grammar';

export const FglCommandName = Object.freeze({
  RC: 'RC', // Row Column
  BX: 'BX', // Box
  NR: 'NR', // No Rotation
  RR: 'RR', // Rotate Right
  RL: 'RL', // Rotate Left
  RU: 'RU', // Rotate Upside Down
  HW: 'HW', // Height Width (scale)
  F: 'F', // Font
  BS: 'BS', // Box Size
  TTF: 'TTF', // Truetype Font, with size
  RTF: 'RTF', // TrueType Font (deprecated)
  SD: 'SD', // Scale Down
  QRV: 'QRV', // QR Version
  QR: 'QR', // QR Code
});

export type FglCommandName = keyof typeof FglCommandName;

export type FglCommand = {
  type: 'command';
  command: FglCommandName;
  params: number[];
  qrText?: string; // Only for QR commands
};

export type FglText = {
  type: 'text';
  text: string;
};

export type FglNode = FglCommand | FglText;

const semantics = fglGrammar.createSemantics().addOperation<FglNode[]>('toAST', {
  Layout: ({ children }) => children.map(c => c.toAST()).flat(),

  GenericCommand: (_open, cmdName, paramsOpt, _close) => {
    if (!(cmdName.sourceString in FglCommandName)) {
      return [];
    }

    const command = cmdName.sourceString as FglCommandName;
    const params = paramsOpt.children.map(c => c.toAST()).flat();

    return [{ type: 'command', command, params } satisfies FglCommand];
  },

  QrCommand: (_open, _qr, paramsOpt, _close, _openBrace, qrTextRaw, _closeBrace) => {
    const qrText = qrTextRaw.sourceString;
    const params = paramsOpt.children.map(c => c.toAST()).flat();
    return [{ type: 'command', command: FglCommandName.QR, params, qrText } satisfies FglCommand];
  },

  paramList: (first, _comma, rest) => {
    return [
      parseInt(first.sourceString),
      ...rest.children.map(c => parseInt(c.sourceString)),
    ];
  },

  CmdName: ({ sourceString }) => sourceString,

  Text: ({ sourceString }) => [{ type: 'text', text: sourceString } satisfies FglText],
});

export const parseFglString = (input: string): FglNode[] => {
  const match = fglGrammar.match(input);
  if (match.failed()) {
    throw new Error(`FGL parsing failed: ${match.message}`);
  }
  return semantics(match).toAST();
};
